import SingleLineText from './ui/SingleLineText';
import LoadingLabel from './LoadingLabel';
import React, { useEffect, useRef, useState } from 'react';
import useAppLanguage from '../i18n';
import { Box, Typography, IconButton } from '@mui/material';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MedImage from './MedImage';

function pad2(n) { return String(n).padStart(2, '0'); }

function isOral(intakeForm) {
    return !intakeForm || intakeForm === 'ORAL';
}

function formatSince(lastTakenAt, intakeForm = 'ORAL', t = value => value) {
    if (!lastTakenAt) return t(isOral(intakeForm) ? '아직 안 먹음' : '아직 안 씀');
    const then = new Date(lastTakenAt);
    const now = new Date();
    const sec = (now - then) / 1000;
    if (sec < 60) return t('방금 전');
    if (sec < 3600) return t('{count}분 전', { count: Math.floor(sec / 60) });
    const sameDay = then.toDateString() === now.toDateString();
    if (sameDay) return t('오늘 {time}', { time: `${pad2(then.getHours())}:${pad2(then.getMinutes())}` });
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (then.toDateString() === yesterday.toDateString()) return t('어제');
    const days = Math.floor((now - then) / 86400000);
    if (days < 30) return t('{count}일 전', { count: days });
    const months = Math.floor(days / 30);
    if (months < 12) return t('{count}달 전', { count: months });
    return t('{count}년 전', { count: Math.floor(months / 12) });
}

function formatLogTime(takenAt) {
    const d = new Date(takenAt);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * 홈 메뉴 리스트의 첫 번째 슬롯으로 들어가는 PRN 확장 카드.
 *
 * Props:
 * - prnMeds: PRN 약 목록 (lastTakenAt 포함)
 * - todayLogs: 오늘 PRN 로그 리스트 [{ logId, medicationId, medicationName, imageUrl, takenAt }]
 * - onChipClick(med): 약 칩 탭 시
 * - onAddClick(): "+ 추가" 칩 탭 시
 * - onHeaderClick(): 헤더 영역 탭 시 (= /prn 이동)
 * - onTodayLogClick(log): 푸터의 오늘 기록 항목 탭 시
 */
export default function PrnHomeCard({
    prnMeds = [], todayLogs = [],
    onChipClick, onAddClick, onHeaderClick, adding = false,
    onTodayLogEdit, onTodayLogDelete,
}) {
    const { t } = useAppLanguage();
    // 정렬: lastTakenAt 최신 순. null은 마지막
    const sorted = [...prnMeds].sort((a, b) => {
        const at = a.lastTakenAt ? new Date(a.lastTakenAt).getTime() : -Infinity;
        const bt = b.lastTakenAt ? new Date(b.lastTakenAt).getTime() : -Infinity;
        return bt - at;
    });

    // 가로 스크롤 — 웹 스크롤바 스타일 인디케이터 (트랙 + thumb).
    // DOM ref 직접 조작 — useState 기반 측정 시 React batching/strict mode로 인해
    // 첫 측정 시점이 어긋나 thumb 위치가 0이 아닌 곳에서 시작하는 버그 회피.
    const scrollRef = useRef(null);
    const thumbRef = useRef(null);
    const trackRef = useRef(null);
    const [showTrack, setShowTrack] = useState(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // 마운트 직후 강제 reset
        el.scrollLeft = 0;
        // 사용자가 직접 만지기 전까지 외부 요인(snap, layout 변경)으로 scrollLeft가
        // 움직이지 않도록 0으로 lock. 한 번이라도 터치/wheel/마우스다운하면 lock 해제.
        let userTouched = false;
        const releaseLock = () => { userTouched = true; };
        el.addEventListener('touchstart', releaseLock, { passive: true });
        el.addEventListener('wheel', releaseLock, { passive: true });
        el.addEventListener('mousedown', releaseLock);

        // PADDING은 컨테이너 px:16 양쪽 = 32. ratio 계산 시 제외해서
        // 실제 칩 콘텐츠 기준 thumb 폭/위치를 정확히 결정.
        const PADDING_TOTAL = 32;

        const update = () => {
            const thumb = thumbRef.current;
            if (!thumb) return;
            // 외부 layout 변경으로 scrollLeft가 변했다면 사용자 터치 전엔 다시 0으로
            if (!userTouched && el.scrollLeft !== 0) {
                el.scrollLeft = 0;
            }
            const sw = el.scrollWidth, cw = el.clientWidth;
            const overflow = sw - cw > 1;
            setShowTrack(overflow);
            if (!overflow) return;
            // padding 제외한 콘텐츠 폭 기준
            const contentVisible = Math.max(1, cw - PADDING_TOTAL);
            const contentScroll = Math.max(1, sw - PADDING_TOTAL);
            const rawThumbW = (contentVisible / contentScroll) * 100;
            const thumbW = Math.max(15, Math.min(100, rawThumbW));
            const maxScroll = Math.max(1, sw - cw);
            const sl = el.scrollLeft < 4 ? 0 : el.scrollLeft;
            const progress = Math.min(1, Math.max(0, sl / maxScroll));
            const thumbL = progress * (100 - thumbW);
            // 직접 DOM 업데이트 — React render cycle 우회로 정확한 0% 보장
            thumb.style.left = `${thumbL}%`;
            thumb.style.width = `${thumbW}%`;
        };
        // RAF로 첫 frame 측정 + 100ms 후 재측정 (font/이미지 layout 안정화)
        const raf = requestAnimationFrame(update);
        const t = setTimeout(update, 100);
        el.addEventListener('scroll', update, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (ro) ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
            el.removeEventListener('scroll', update);
            el.removeEventListener('touchstart', releaseLock);
            el.removeEventListener('wheel', releaseLock);
            el.removeEventListener('mousedown', releaseLock);
            if (ro) ro.disconnect();
        };
    }, [sorted.length]);

    return (
        <Box sx={{
            mx: 2, mt: 2,
            borderRadius: '16px',
            background: 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)',
            border: '1px solid #bae6fd',
            overflow: 'hidden',
        }}>
            {/* 헤더 */}
            <Box
                onClick={onHeaderClick}
                role="button"
                tabIndex={0}
                sx={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '14px 16px 10px',
                    cursor: 'pointer',
                    '&:active': { opacity: 0.7 },
                }}
            >
                <Box sx={{
                    width: 36, height: 36, borderRadius: '12px', flexShrink: 0,
                    bgcolor: '#e0f2fe',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <TouchAppIcon sx={{ fontSize: 20, color: '#38bdf8' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <SingleLineText sx={{
                        fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em',
                        color: '#0c4a6e', lineHeight: 1.3,
                    }}>
                        {t('필요할 때 먹는 약')}
                    </SingleLineText>
                    <Typography sx={{
                        fontSize: 13, fontWeight: 500, letterSpacing: '-0.01em',
                        color: '#3f6c8a', lineHeight: 1.45, mt: '2px',
                    }}>
                        {t('{count}개 · 약을 눌러 기록하기', { count: prnMeds.length })}
                    </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 18, color: '#7dd3fc', flexShrink: 0 }} />
            </Box>

            {/* 칩 가로 스크롤 + 하단 웹 스크롤바 스타일 인디케이터 (트랙+thumb).
                헤더 우측 ChevronRight와 시각적으로 겹치지 않게 별도 가로 트랙으로 표시. */}
            <Box
                ref={scrollRef}
                sx={{
                display: 'flex', gap: '8px', px: '16px',
                overflowX: 'auto',
                // scrollSnap 제거 — 마운트 시 자동 snap으로 scrollLeft가 양수가 되어
                // 인디케이터 thumb이 0%에서 시작 안 하는 버그 원인. 자연 스크롤로 충분.
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}>
                {sorted.map(med => (
                    <Box
                        key={med.id}
                        component="button"
                        type="button"
                        onClick={() => onChipClick && onChipClick(med)}
                        data-tappable="true"
                        sx={{
                            flexShrink: 0,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                            minWidth: 76, maxWidth: 90,
                            border: 'none', cursor: 'pointer',
                            bgcolor: '#fff',
                            borderRadius: '14px',
                            border: '1px solid #e0f2fe',
                            py: '10px', px: '8px',
                            '&:active': { transform: 'scale(0.96)', bgcolor: '#f0f9ff' },
                            transition: 'transform 0.1s ease',
                        }}
                    >
                        <MedImage src={med.imageUrl} size={36} />
                        <Typography data-medication-name-preview sx={{
                            fontSize: 12, fontWeight: 700, color: '#1a1a1a',
                            lineHeight: 1.2, textAlign: 'center',
                            width: '100%',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{med.name}</Typography>
                        <Typography sx={{
                            fontSize: 10, color: '#3f6c8a', fontWeight: 600,
                            lineHeight: 1.1,
                        }}>{formatSince(med.lastTakenAt, med.intakeForm, t)}</Typography>
                    </Box>
                ))}
                {/* + 추가 칩 */}
                <Box
                    component="button"
                    type="button"
                    disabled={adding}
                    onClick={onAddClick}
                    aria-label={t('비상약 추가')}
                    sx={{
                        flexShrink: 0,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: '4px',
                        minWidth: 76, maxWidth: 90,
                        border: '1.5px dashed #bae6fd',
                        bgcolor: 'transparent',
                        borderRadius: '14px',
                        py: '10px', px: '8px', cursor: 'pointer',
                        '&:active': { transform: 'scale(0.96)' },
                    }}
                >
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        bgcolor: '#e0f2fe',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {adding ? <LoadingLabel>{null}</LoadingLabel> : <AddIcon sx={{ fontSize: 20, color: '#38bdf8' }} />}
                    </Box>
                    <SingleLineText sx={{
                        fontSize: 12, fontWeight: 700, color: '#0369a1',
                        lineHeight: 1.2, mt: '2px',
                    }}>{t('추가')}</SingleLineText>
                </Box>
            </Box>

            {/* 웹 스크롤바 스타일 인디케이터 — 오버플로일 때만 표시.
                DOM ref 직접 조작 (위 useEffect) — transition 없음으로 0%에서 정확히 시작. */}
            <Box
                ref={trackRef}
                sx={{
                    mx: '16px', mt: '8px', mb: '12px',
                    height: 3, bgcolor: '#E5E7EB', borderRadius: 999,
                    position: 'relative', overflow: 'hidden',
                    visibility: showTrack ? 'visible' : 'hidden',
                }}
            >
                <Box
                    ref={thumbRef}
                    sx={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: '0%', width: '15%',
                        bgcolor: '#9CA3AF', borderRadius: 999,
                    }}
                />
            </Box>

            {/* 오늘 복용 로그 — 카드 행 형태로 노출 (홈 "완료된 것들" 섹션과 통합) */}
            {todayLogs.length > 0 && (
                <Box sx={{
                    borderTop: '1px solid #e0f2fe',
                    bgcolor: 'rgba(224, 242, 254, 0.25)',
                    px: '12px', py: '10px',
                    display: 'flex', flexDirection: 'column', gap: '6px',
                }}>
                    <SingleLineText sx={{
                        fontSize: 11, fontWeight: 700, color: '#3f6c8a',
                        pl: '4px', mb: '2px',
                    }}>{t('오늘 챙김')}</SingleLineText>
                    {todayLogs.map(log => (
                        <Box key={log.logId} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.2,
                            p: '10px 12px', borderRadius: '12px',
                            bgcolor: '#fff',
                            border: '1px solid #bae6fd',
                        }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                bgcolor: '#e0f2fe',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#38bdf8' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontSize: '0.95rem', fontWeight: 700, color: '#0c4a6e',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {log.medicationName}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '0.85rem', color: '#3f6c8a', mt: '1px',
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {t('{time} 챙김', { time: formatLogTime(log.takenAt) })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                                <Box
                                    role="button" tabIndex={0}
                                    onClick={() => onTodayLogEdit && onTodayLogEdit(log)}
                                    sx={{
                                        px: 1.2, py: 0.5, borderRadius: 2,
                                        cursor: 'pointer', bgcolor: '#fff',
                                        border: '1px solid #bae6fd',
                                        '&:active': { opacity: 0.5, bgcolor: '#f0f9ff' },
                                    }}>
                                    <SingleLineText sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0369a1' }}>
                                        {t('시간 수정')}
                                    </SingleLineText>
                                </Box>
                                <IconButton
                                    aria-label={t('기록 삭제')}
                                    onClick={() => onTodayLogDelete && onTodayLogDelete(log)}
                                    sx={{
                                        width: 32, height: 32,
                                        bgcolor: '#fff', border: '1px solid #fecaca',
                                        borderRadius: 2,
                                        '&:active': { bgcolor: '#fef2f2' },
                                    }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
